import { useEffect, useMemo, useState } from 'react';
import { useApiUrl } from '@refinedev/core';
import { useQuery } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { z } from 'zod';
import { CalendarDays, CheckCircle2, Loader2, Tags, TrendingUp, UserCheck, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LeadFunnelStageFields } from './funnels/form';
import type { LeadStatusConfig } from './types';

const ANALYTICS_PAGE_SIZE = 100;
const CHART_DAYS = 14;

const analyticsLeadTagSchema = z.object({
  id: z.number(),
  tag: z.object({
    id: z.number(),
    name: z.string(),
    color: z.string(),
  }),
});

const analyticsLeadSchema = z.object({
  id: z.number(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  telegramUsername: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  assignedAdmin: z.object({
    id: z.number(),
    username: z.string(),
  }).nullable(),
  tags: z.array(analyticsLeadTagSchema).default([]),
  convertedUserId: z.number().nullable(),
  convertedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const leadsPageSchema = z.object({
  success: z.literal(true),
  data: z.array(analyticsLeadSchema),
  meta: z.object({
    page: z.number(),
    perPage: z.number(),
    total: z.number(),
    totalPages: z.number(),
  }),
});

type AnalyticsLead = z.infer<typeof analyticsLeadSchema>;

interface LeadStageAnalyticsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: LeadStatusConfig | null;
  kanbanQuery: Record<string, string>;
  onStageSaved: () => void;
}

interface LeadsPage {
  items: AnalyticsLead[];
  total: number;
  totalPages: number;
}

interface FetchLeadsPageParams {
  apiUrl: string;
  statusId: number;
  kanbanQuery: Record<string, string>;
  page: number;
  signal: AbortSignal;
}

interface LeadStageAnalytics {
  total: number;
  assigned: number;
  unassigned: number;
  converted: number;
  createdToday: number;
  createdLastWeek: number;
  conversionRate: number;
  byManager: Array<{ name: string; count: number; converted: number }>;
  byTag: Array<{ name: string; color: string; count: number }>;
  byDay: Array<{ date: string; label: string; count: number }>;
  latestLeads: AnalyticsLead[];
}

const numberFormatter = new Intl.NumberFormat('ru-RU');
const percentFormatter = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatNumber(value: number): string {
  return numberFormatter.format(value);
}

function formatPercent(value: number): string {
  return `${percentFormatter.format(value)}%`;
}

function dateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getLeadDisplayName(lead: AnalyticsLead): string {
  const name = [lead.firstName, lead.lastName].filter(Boolean).join(' ').trim();
  if (name) { return name; }
  if (lead.email) { return lead.email; }
  if (lead.telegramUsername) { return `@${lead.telegramUsername}`; }
  if (lead.phone) { return lead.phone; }
  return `Лид #${lead.id}`;
}

async function fetchLeadsPage({ apiUrl, statusId, kanbanQuery, page, signal }: FetchLeadsPageParams): Promise<LeadsPage> {
  const params = new URLSearchParams({
    ...kanbanQuery,
    statusConfigId: String(statusId),
    page: String(page),
    limit: String(ANALYTICS_PAGE_SIZE),
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const response = await fetch(`${apiUrl}/leads?${params.toString()}`, {
    credentials: 'include',
    signal,
  });
  if (!response.ok) {
    throw new Error('Не удалось загрузить аналитику этапа');
  }
  const body = leadsPageSchema.parse(await response.json());
  return {
    items: body.data,
    total: body.meta.total,
    totalPages: Math.max(1, body.meta.totalPages),
  };
}

async function fetchAllStageLeads(params: Omit<FetchLeadsPageParams, 'page'>): Promise<AnalyticsLead[]> {
  const firstPage = await fetchLeadsPage({ ...params, page: 1 });
  if (firstPage.totalPages <= 1) {
    return firstPage.items;
  }

  const remainingPages = Array.from({ length: firstPage.totalPages - 1 }, (_, index) => index + 2);
  const pages = await Promise.all(
    remainingPages.map((page) => fetchLeadsPage({ ...params, page })),
  );
  return [firstPage.items, ...pages.map((page) => page.items)].flat();
}

function buildDayChart(leads: AnalyticsLead[]): LeadStageAnalytics['byDay'] {
  const today = new Date();
  const counts = new Map<string, number>();
  for (let index = CHART_DAYS - 1; index >= 0; index -= 1) {
    const day = new Date(today);
    day.setDate(today.getDate() - index);
    counts.set(dateKey(day), 0);
  }

  for (const lead of leads) {
    const key = dateKey(new Date(lead.createdAt));
    if (counts.has(key)) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }

  return [...counts.entries()].map(([date, count]) => ({
    date,
    label: date.slice(5),
    count,
  }));
}

function buildAnalytics(leads: AnalyticsLead[]): LeadStageAnalytics {
  const now = new Date();
  const today = dateKey(now);
  const weekAgo = new Date(now);
  weekAgo.setDate(now.getDate() - 7);

  const byManager = new Map<string, { count: number; converted: number }>();
  const byTag = new Map<string, { color: string; count: number }>();

  let assigned = 0;
  let converted = 0;
  let createdToday = 0;
  let createdLastWeek = 0;

  for (const lead of leads) {
    const leadCreatedAt = new Date(lead.createdAt);
    const managerName = lead.assignedAdmin?.username ?? 'Без менеджера';
    const managerRow = byManager.get(managerName) ?? { count: 0, converted: 0 };
    managerRow.count += 1;

    if (lead.assignedAdmin !== null) {
      assigned += 1;
    }
    if (lead.convertedAt !== null || lead.convertedUserId !== null) {
      converted += 1;
      managerRow.converted += 1;
    }
    if (dateKey(leadCreatedAt) === today) {
      createdToday += 1;
    }
    if (leadCreatedAt >= weekAgo) {
      createdLastWeek += 1;
    }

    byManager.set(managerName, managerRow);
    for (const leadTag of lead.tags) {
      const tagRow = byTag.get(leadTag.tag.name) ?? { color: leadTag.tag.color, count: 0 };
      tagRow.count += 1;
      byTag.set(leadTag.tag.name, tagRow);
    }
  }

  const total = leads.length;
  return {
    total,
    assigned,
    unassigned: total - assigned,
    converted,
    createdToday,
    createdLastWeek,
    conversionRate: total === 0 ? 0 : (converted / total) * 100,
    byManager: [...byManager.entries()]
      .map(([name, row]) => ({ name, count: row.count, converted: row.converted }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
    byTag: [...byTag.entries()]
      .map(([name, row]) => ({ name, color: row.color, count: row.count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
    byDay: buildDayChart(leads),
    latestLeads: leads.slice(0, 6),
  };
}

export function LeadStageAnalyticsDialog({
  open,
  onOpenChange,
  status,
  kanbanQuery,
  onStageSaved,
}: LeadStageAnalyticsDialogProps) {
  const apiUrl = useApiUrl();
  const [tab, setTab] = useState<'analytics' | 'edit'>('analytics');
  const statusId = status?.id ?? 0;

  useEffect(() => {
    if (open) {
      setTab('analytics');
    }
  }, [open, statusId]);

  const analyticsQuery = useQuery({
    queryKey: ['lead-stage-analytics', statusId, kanbanQuery],
    enabled: open && tab === 'analytics' && status !== null,
    staleTime: 30_000,
    queryFn: ({ signal }) => fetchAllStageLeads({
      apiUrl,
      statusId,
      kanbanQuery,
      signal,
    }),
  });

  const analytics = useMemo(
    () => buildAnalytics(analyticsQuery.data ?? []),
    [analyticsQuery.data],
  );

  const handleTabChange = (value: string) => {
    if (value === 'analytics' || value === 'edit') {
      setTab(value);
    }
  };

  const handleStageSaved = () => {
    onStageSaved();
    void analyticsQuery.refetch();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {status && (
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: status.color }} />
            )}
            {status?.name ?? 'Этап воронки'}
          </DialogTitle>
          <DialogDescription>
            Аналитика этапа и настройки колонки воронки.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="analytics">
              <TrendingUp className="mr-1.5 h-4 w-4" />
              Аналитика
            </TabsTrigger>
            <TabsTrigger value="edit">Редактирование</TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="space-y-4 pt-2">
            {analyticsQuery.isLoading && (
              <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Загрузка аналитики...
              </div>
            )}

            {analyticsQuery.isError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                Не удалось загрузить аналитику этапа.
              </div>
            )}

            {!analyticsQuery.isLoading && !analyticsQuery.isError && (
              <>
                <div className="grid gap-2 sm:grid-cols-4">
                  <Card>
                    <CardContent className="flex flex-col gap-1 px-3 py-3">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Users className="h-3 w-3" />
                        Лидов
                      </div>
                      <div className="font-mono text-xl font-semibold">{formatNumber(analytics.total)}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="flex flex-col gap-1 px-3 py-3">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <UserCheck className="h-3 w-3" />
                        Назначено
                      </div>
                      <div className="font-mono text-xl font-semibold">{formatNumber(analytics.assigned)}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="flex flex-col gap-1 px-3 py-3">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CheckCircle2 className="h-3 w-3" />
                        Конверсия
                      </div>
                      <div className="font-mono text-xl font-semibold">{formatPercent(analytics.conversionRate)}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="flex flex-col gap-1 px-3 py-3">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CalendarDays className="h-3 w-3" />
                        За 7 дней
                      </div>
                      <div className="font-mono text-xl font-semibold">{formatNumber(analytics.createdLastWeek)}</div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-muted-foreground">Динамика лидов</div>
                    <div className="h-[220px] rounded-lg border border-border/60 p-3">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.byDay}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                          <YAxis allowDecimals={false} fontSize={11} tickLine={false} axisLine={false} />
                          <Tooltip />
                          <Bar dataKey="count" fill={status?.color ?? '#6b7280'} radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="text-sm font-medium text-muted-foreground">Срезы</div>
                    <div className="rounded-lg border border-border/60 p-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Без менеджера</span>
                        <span className="font-mono font-medium">{formatNumber(analytics.unassigned)}</span>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-muted-foreground">Создано сегодня</span>
                        <span className="font-mono font-medium">{formatNumber(analytics.createdToday)}</span>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-muted-foreground">Сконвертировано</span>
                        <span className="font-mono font-medium">{formatNumber(analytics.converted)}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {status?.isInitial && <Badge variant="secondary">Начальный</Badge>}
                      {status?.isFinal && <Badge variant="secondary">Финальный</Badge>}
                      {status?.isSystem && <Badge variant="outline">Системный</Badge>}
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-muted-foreground">По менеджерам</div>
                    <div className="space-y-1.5">
                      {analytics.byManager.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">Нет лидов</div>
                      ) : (
                        analytics.byManager.map((manager) => (
                          <div key={manager.name} className="rounded-lg border border-border/60 p-2.5">
                            <div className="flex items-center justify-between gap-3">
                              <span className="truncate text-sm font-medium">{manager.name}</span>
                              <span className="font-mono text-sm">{formatNumber(manager.count)}</span>
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              Конвертировано: {formatNumber(manager.converted)}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
                      <Tags className="h-3.5 w-3.5" />
                      По тегам
                    </div>
                    <div className="space-y-1.5">
                      {analytics.byTag.length === 0 ? (
                        <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">Нет тегов</div>
                      ) : (
                        analytics.byTag.map((tag) => (
                          <div key={tag.name} className="flex items-center justify-between rounded-lg border border-border/60 p-2.5">
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: tag.color }} />
                              <span className="truncate text-sm font-medium">{tag.name}</span>
                            </div>
                            <span className="font-mono text-sm">{formatNumber(tag.count)}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">Последние лиды</div>
                  <div className="space-y-1.5">
                    {analytics.latestLeads.length === 0 ? (
                      <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">Нет лидов</div>
                    ) : (
                      analytics.latestLeads.map((lead) => (
                        <div key={lead.id} className="flex items-center justify-between gap-3 rounded-lg border border-border/60 p-2.5">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium">{getLeadDisplayName(lead)}</div>
                            <div className="truncate text-xs text-muted-foreground">
                              {lead.assignedAdmin?.username ?? 'Без менеджера'}
                            </div>
                          </div>
                          {lead.convertedAt && <Badge variant="secondary">Конвертирован</Badge>}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="edit" className="space-y-4 pt-2">
            {status && (
              <LeadFunnelStageFields
                funnelId={status.leadFunnelId}
                status={status}
                position={status.position}
                onCancel={() => onOpenChange(false)}
                onSuccess={handleStageSaved}
              />
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
